import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Copy, Link2, Mail, RefreshCw } from 'lucide-react';

interface AdminAccessLinkPanelProps {
  userId: string;
  userEmail: string;
}

export function AdminAccessLinkPanel({ userId, userEmail }: AdminAccessLinkPanelProps) {
  const [linkType, setLinkType] = useState<'magiclink' | 'recovery' | 'invite'>('magiclink');
  const [loading, setLoading] = useState(false);
  const [actionLink, setActionLink] = useState<string | null>(null);
  const [lastMessage, setLastMessage] = useState<string | null>(null);

  const generate = async () => {
    setLoading(true);
    setActionLink(null);
    setLastMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke('admin-generate-access-link', {
        body: { userId, linkType },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setLastMessage(data.message ?? 'Listo');
      if (data.actionLink) setActionLink(data.actionLink);
      toast.success(data.message ?? 'Enlace generado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al generar acceso');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!actionLink) return;
    try {
      await navigator.clipboard.writeText(actionLink);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <div className="rounded-xl border bg-muted/20 p-4 space-y-3">
      <div>
        <p className="text-sm font-medium flex items-center gap-2">
          <Link2 className="h-4 w-4 text-primary" />
          Acceso e inicio de sesión
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Genera un enlace o reenvía invitación para {userEmail}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={linkType} onValueChange={(v) => setLinkType(v as typeof linkType)}>
          <SelectTrigger className="sm:flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="magiclink">Enlace mágico (acceso directo)</SelectItem>
            <SelectItem value="recovery">Restablecer contraseña</SelectItem>
            <SelectItem value="invite">Reenviar invitación por correo</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="secondary"
          className="gap-2 shrink-0"
          disabled={loading}
          onClick={generate}
        >
          {loading ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : linkType === 'invite' ? (
            <Mail className="h-4 w-4" />
          ) : (
            <Link2 className="h-4 w-4" />
          )}
          {linkType === 'invite' ? 'Enviar correo' : 'Generar enlace'}
        </Button>
      </div>

      {lastMessage && !actionLink && (
        <p className="text-xs text-muted-foreground">{lastMessage}</p>
      )}

      {actionLink && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Enlace (compartir de forma segura)</Label>
          <div className="flex gap-2">
            <Input readOnly value={actionLink} className="text-xs font-mono" />
            <Button type="button" variant="outline" size="icon" onClick={copyLink}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
